import { useQuery, keepPreviousData, useMutation, UseQueryOptions } from '@tanstack/react-query'
import { ObjectProperty } from '../../shared/types'
import { del, get, post, put } from '../fetch'
import { ParamsType } from '../scaffolding/types'

const TTL = 1000 * 60 * 60

export type ObjectPropertiesParamsType = {
	id?: string
	name?: string
	identifier?: string
	object: string
}

export function useObjectPropertyList(searchParams: ParamsType & ObjectPropertiesParamsType) {
	return useQuery({
		queryKey: ['api/object-properties', searchParams],
		queryFn: () =>
			post<{ total: number; list: ObjectProperty[] }>(
				`/api/0/object-properties`,
				searchParams,
			),
		placeholderData: keepPreviousData,
		staleTime: TTL,
		gcTime: TTL,
	})
}

export function useObjectPropertyListAll<T = ObjectProperty[]>(
	object: string,
	options?: Omit<UseQueryOptions<ObjectProperty[], Error, T>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		...options,
		queryKey: ['api/object-properties', object],
		queryFn: () => get<ObjectProperty[]>(`/api/0/object-properties?object=${object}`),
		staleTime: TTL,
		gcTime: TTL,
	})
}

export function useObjectPropertyGet(id?: string) {
	return useQuery({
		queryKey: ['api/object-properties', id],
		queryFn: () => get<ObjectProperty>(`/api/0/object-properties/${id}`),
		enabled: !!id,
		staleTime: TTL,
		gcTime: TTL,
	})
}

export function useObjectPropertyCreate() {
	return useMutation({
		mutationFn: (data: FormData) => put('/api/0/object-properties', data),
		meta: {
			invalidateQueries: [['api/object-properties']],
			notifications: {
				success: () => 'Vlastnost vytvořena',
			},
		},
	})
}

export function useObjectPropertyUpdate(id: string) {
	return useMutation({
		mutationFn: (data: FormData) => post(`/api/0/object-properties/${id}`, data),
		meta: {
			invalidateQueries: [['api/object-properties']],
			notifications: {
				success: () => 'Změny uloženy',
			},
		},
	})
}

export function useObjectPropertyRemove() {
	return useMutation({
		mutationFn: (id: string) => del(`/api/0/object-properties/${id}`),
		meta: {
			invalidateQueries: [['api/object-properties']],
			notifications: {
				success: () => 'Vlastnost smazána',
			},
		},
	})
}
